/**
 * The samagri catalogue — `/v1/admin/samagri` items and kits (content 07 §3, CSR-009).
 *
 * The server lints each write, derives a kit's item count and estimated cost from its lines, and
 * audits; this file only moves the request. Every write invalidates the whole `samagri` key,
 * because an item's cost changes every kit that holds it.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { keys } from '../queryClient';
import type { components } from '../../../api/generated/schema';

type Schemas = components['schemas'];
export type SamagriItemRow = Schemas['SamagriItemRow'];
export type SamagriItemIn = Schemas['SamagriItemIn'];
export type SamagriItemPatch = Schemas['SamagriItemPatch'];
export type SamagriKitRow = Schemas['SamagriKitRow'];
export type SamagriKitDetail = Schemas['SamagriKitDetail'];
export type SamagriKitIn = Schemas['SamagriKitIn'];
export type SamagriKitPatch = Schemas['SamagriKitPatch'];
export type SamagriKitLineIn = Schemas['SamagriKitLine-Input'];
export type SamagriCategory = SamagriItemIn['category'];
export type SamagriItemKind = NonNullable<SamagriItemIn['item_kind']>;
export type SamagriRegion = NonNullable<SamagriKitIn['region_variant']>;

const BASE = '/v1/admin/samagri';

export function useSamagriItems() {
  return useQuery({
    queryKey: keys.samagri.items(),
    queryFn: () => authedClient.request<Schemas['SamagriItemList']>(`${BASE}/items`),
  });
}

export function useSamagriKits() {
  return useQuery({
    queryKey: keys.samagri.kits(),
    queryFn: () => authedClient.request<Schemas['SamagriKitList']>(`${BASE}/kits`),
  });
}

export function useSamagriKit(id: number) {
  return useQuery({
    queryKey: keys.samagri.kit(id),
    queryFn: () => authedClient.request<SamagriKitDetail>(`${BASE}/kits/${id}`),
  });
}

function useWrite<TBody, TOut>(request: (body: TBody) => Promise<TOut>) {
  const client = useQueryClient();
  return useMutation<TOut, unknown, TBody>({
    mutationFn: request,
    onSuccess: () => client.invalidateQueries({ queryKey: keys.samagri.all() }),
  });
}

export const useCreateSamagriItem = () =>
  useWrite((body: SamagriItemIn) =>
    authedClient.request<SamagriItemRow>(`${BASE}/items`, { method: 'POST', body }),
  );

export const useUpdateSamagriItem = () =>
  useWrite(({ id, patch }: { id: number; patch: SamagriItemPatch }) =>
    authedClient.request<SamagriItemRow>(`${BASE}/items/${id}`, { method: 'PATCH', body: patch }),
  );

export const useCreateSamagriKit = () =>
  useWrite((body: SamagriKitIn) =>
    authedClient.request<SamagriKitRow>(`${BASE}/kits`, { method: 'POST', body }),
  );

export const useUpdateSamagriKit = () =>
  useWrite(({ id, patch }: { id: number; patch: SamagriKitPatch }) =>
    authedClient.request<SamagriKitRow>(`${BASE}/kits/${id}`, { method: 'PATCH', body: patch }),
  );

export const useReplaceKitLines = () =>
  useWrite(({ id, items }: { id: number; items: SamagriKitLineIn[] }) =>
    authedClient.request<SamagriKitDetail>(`${BASE}/kits/${id}/items`, { method: 'PUT', body: { items } }),
  );
